import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import { volatilityService, metalsService, globalShockService } from '../../../services/metalguard';
import { SkeletonRow as SharedSkeletonRow } from '../../../components/skeletons';

const METAL_COLOR_MAP = {
  'Cobre': '#EA580C', 'Aluminio': '#3B82F6', 'Zinc': '#059669', 'Plomo': '#6B7280',
  'Niquel': '#7C3AED', 'Acero Inox 304': '#64748B', 'Hierro/Acero': '#475569',
  'Laton CW614N': '#D97706', 'Bronce CuSn8': '#92400E',
};

const DEFAULT_GLOBAL = {
  shock_scenario_mild_pct: -3,
  shock_scenario_moderate_pct: -5,
  shock_scenario_severe_pct: -10,
  eurusd_shock_pct: -2,
  use_per_metal_volatility: true,
};

function Toggle({ checked, onChange, label }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 ${
        checked ? 'bg-accent' : 'bg-muted-foreground/30'
      }`}
      title={label}
    >
      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform ${
        checked ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

function SkeletonRow() {
  return <SharedSkeletonRow cols={7} />;
}

function ValidationError({ msg }) {
  if (!msg) return null;
  return <p className="text-error mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem' }}>{msg}</p>;
}

function validateVolRow(row) {
  const errors = {};
  const v7 = parseFloat(row?.volatility_pct_7d);
  const v14 = parseFloat(row?.volatility_pct_14d);
  const v30 = parseFloat(row?.volatility_pct_30d);
  const cs = parseFloat(row?.custom_shock_override);
  if (isNaN(v7) || v7 < 0 || v7 > 100) errors.volatility_pct_7d = 'Debe estar entre 0 y 100';
  if (isNaN(v14) || v14 < 0 || v14 > 100) errors.volatility_pct_14d = 'Debe estar entre 0 y 100';
  if (isNaN(v30) || v30 < 0 || v30 > 100) errors.volatility_pct_30d = 'Debe estar entre 0 y 100';
  if (row?.use_custom_shock) {
    if (isNaN(cs) || cs < 0 || cs > 50) errors.custom_shock_override = 'Debe estar entre 0 y 50';
  }
  return errors;
}

// ─── Add Row Modal ────────────────────────────────────────────────────────────

function AddRowModal({ metals, existingMetalIds, onClose, onSave }) {
  const [form, setForm] = useState({
    metal_id: '',
    volatility_pct_7d: '2.0',
    volatility_pct_14d: '3.5',
    volatility_pct_30d: '5.5',
    custom_shock_override: '5',
    use_custom_shock: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const availableMetals = metals?.filter(m => !existingMetalIds?.includes(m?.id));

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      const errs = validateVolRow({ ...form, [field]: value });
      setErrors(prev => ({ ...prev, [field]: errs?.[field] || null }));
    } else {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'metal_id') {
      if (!form?.metal_id) setErrors(prev => ({ ...prev, metal_id: 'Selecciona un metal' }));
      else setErrors(prev => ({ ...prev, metal_id: null }));
      return;
    }
    const errs = validateVolRow(form);
    setErrors(prev => ({ ...prev, [field]: errs?.[field] || null }));
  };

  const handleSave = async () => {
    if (!form?.metal_id) {
      setErrors({ metal_id: 'Selecciona un metal' });
      setTouched(prev => ({ ...prev, metal_id: true }));
      return;
    }
    const errs = validateVolRow(form);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      const allTouched = {};
      Object.keys(errs)?.forEach(k => { allTouched[k] = true; });
      setTouched(prev => ({ ...prev, ...allTouched }));
      return;
    }
    setSaving(true);
    await onSave({
      metal_id: form?.metal_id,
      volatility_pct_7d: parseFloat(form?.volatility_pct_7d),
      volatility_pct_14d: parseFloat(form?.volatility_pct_14d),
      volatility_pct_30d: parseFloat(form?.volatility_pct_30d),
      custom_shock_override: form?.use_custom_shock ? parseFloat(form?.custom_shock_override) : null,
      use_custom_shock: form?.use_custom_shock,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div className="w-full max-w-md rounded-xl border border-border" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-xl)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Plus" size={18} color="var(--color-accent)" strokeWidth={2.5} />
            <h3 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Nuevo Parametro de Volatilidad</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Cerrar">
            <Icon name="X" size={18} color="currentColor" strokeWidth={2} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Metal selector */}
          <div>
            <label className="block text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Metal *</label>
            <select
              value={form?.metal_id}
              onChange={e => set('metal_id', e?.target?.value)}
              onBlur={() => handleBlur('metal_id')}
              className={`w-full bg-background border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent ${
                errors?.metal_id ? 'border-red-500' : 'border-border'
              }`}
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', borderColor: errors?.metal_id ? 'var(--color-error)' : undefined }}
            >
              <option value="">Seleccionar metal...</option>
              {availableMetals?.map(m => (
                <option key={m?.id} value={m?.id}>{m?.name}</option>
              ))}
            </select>
            <ValidationError msg={errors?.metal_id} />
            {availableMetals?.length === 0 && (
              <p className="text-muted-foreground mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>Todos los metales ya tienen configuracion.</p>
            )}
          </div>
          {/* Volatility fields */}
          {[
            { field: 'volatility_pct_7d', label: 'Volatilidad 7d (%)' },
            { field: 'volatility_pct_14d', label: 'Volatilidad 14d (%)' },
            { field: 'volatility_pct_30d', label: 'Volatilidad 30d (%)' },
          ]?.map(({ field, label }) => (
            <div key={field}>
              <label className="block text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>{label}</label>
              <input
                type="number" min="0" max="100" step="0.1"
                value={form?.[field]}
                onChange={e => set(field, e?.target?.value)}
                onBlur={() => handleBlur(field)}
                className={`w-full bg-background border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent ${
                  errors?.[field] ? 'border-red-500' : 'border-border'
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', borderColor: errors?.[field] ? 'var(--color-error)' : undefined }}
              />
              <ValidationError msg={errors?.[field]} />
            </div>
          ))}
          {/* Custom shock */}
          <div className="flex items-center justify-between py-2 border-t border-border">
            <span className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Usar Shock Custom</span>
            <Toggle checked={form?.use_custom_shock} onChange={v => set('use_custom_shock', v)} label="Activar shock personalizado" />
          </div>
          {form?.use_custom_shock && (
            <div>
              <label className="block text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Override Shock Custom (%) — máx. 50</label>
              <input
                type="number" min="0" max="50" step="0.5"
                value={form?.custom_shock_override}
                onChange={e => set('custom_shock_override', e?.target?.value)}
                onBlur={() => handleBlur('custom_shock_override')}
                className={`w-full bg-background border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent ${
                  errors?.custom_shock_override ? 'border-red-500' : 'border-border'
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', borderColor: errors?.custom_shock_override ? 'var(--color-error)' : undefined }}
              />
              <ValidationError msg={errors?.custom_shock_override} />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-border transition-colors" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving || availableMetals?.length === 0}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-white disabled:opacity-60 transition-colors"
            style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            {saving ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> : <Icon name="Plus" size={14} color="white" strokeWidth={2.5} />}
            {saving ? 'Guardando...' : 'Agregar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

function DeleteConfirmDialog({ metalName, onClose, onConfirm, deleting }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
      <div className="w-full max-w-sm rounded-xl border border-border" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-xl)' }}>
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'rgba(239,68,68,0.12)' }}>
              <Icon name="Trash2" size={18} color="var(--color-error)" strokeWidth={2} />
            </div>
            <h3 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Eliminar configuracion</h3>
          </div>
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
            ¿Eliminar los parametros de volatilidad para <strong className="text-foreground">{metalName}</strong>? Esta accion no se puede deshacer.
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-border transition-colors" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Cancelar</button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-white disabled:opacity-60 transition-colors"
            style={{ background: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            {deleting ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> : <Icon name="Trash2" size={14} color="white" strokeWidth={2.5} />}
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Editable Cell ─────────────────────────────────────────────────────

function NumericCell({ value, editing, onChange, min, max, step, error }) {
  if (!editing) {
    return (
      <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}>
        {parseFloat(value)?.toFixed(2)}%
      </span>
    );
  }
  return (
    <div>
      <input
        type="number" min={min} max={max} step={step ?? 0.1}
        value={value}
        onChange={e => onChange(e?.target?.value)}
        className={`w-20 bg-background border rounded px-2 py-1 text-foreground focus:outline-none ${
          error ? 'border-error' : 'border-accent'
        }`}
        style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' }}
      />
      {error && <p className="text-error" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.625rem' }}>{error}</p>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ShockVolatilityParams({ onRegisterSave, onToast }) {
  const [rows, setRows] = useState([]);
  const [metals, setMetals] = useState([]);
  const [global, setGlobal] = useState(DEFAULT_GLOBAL);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [globalErrors, setGlobalErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [volRes, metalsRes, globalRes] = await Promise.all([
      volatilityService?.getAll(),
      metalsService?.getAll(),
      globalShockService?.get(),
    ]);
    setMetals(metalsRes?.data || []);
    const volRows = (volRes?.data || [])?.map(v => ({
      id: v?.id,
      metal_id: v?.metal_id,
      metalName: v?.metals?.name || '—',
      metalColor: METAL_COLOR_MAP?.[v?.metals?.name] || '#64748b',
      volatility_pct_7d: parseFloat(v?.volatility_pct_7d ?? 2),
      volatility_pct_14d: parseFloat(v?.volatility_pct_14d ?? 3.5),
      volatility_pct_30d: parseFloat(v?.volatility_pct_30d ?? 5.5),
      custom_shock_override: v?.custom_shock_override != null ? parseFloat(v?.custom_shock_override) : 5,
      use_custom_shock: v?.use_custom_shock ?? false,
    }));
    setRows(volRows);
    if (globalRes?.data?.global_shock_settings) {
      setGlobal({ ...DEFAULT_GLOBAL, ...globalRes?.data?.global_shock_settings });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Register save for parent Settings page (saves global shock only — per-row saves are immediate)
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => async () => {
      const errs = validateGlobal(global);
      if (Object.keys(errs)?.length > 0) throw new Error('Valores globales invalidos');
      const { error } = await globalShockService?.upsert(global);
      if (error) throw new Error(error?.message);
    });
  }, [global, onRegisterSave]);

  // ─── Inline Edit ────────────────────────────────────────────────────────────

  const startEdit = (row) => {
    setEditingId(row?.id);
    setEditDraft({
      volatility_pct_7d: String(row?.volatility_pct_7d),
      volatility_pct_14d: String(row?.volatility_pct_14d),
      volatility_pct_30d: String(row?.volatility_pct_30d),
      custom_shock_override: String(row?.custom_shock_override),
      use_custom_shock: row?.use_custom_shock,
    });
    setEditErrors({});
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); setEditErrors({}); };

  const confirmEdit = async (row) => {
    const errs = validateVolRow(editDraft);
    if (Object.keys(errs)?.length > 0) { setEditErrors(errs); return; }
    setSavingEdit(true);
    const payload = {
      volatility_pct_7d: parseFloat(editDraft?.volatility_pct_7d),
      volatility_pct_14d: parseFloat(editDraft?.volatility_pct_14d),
      volatility_pct_30d: parseFloat(editDraft?.volatility_pct_30d),
      use_custom_shock: editDraft?.use_custom_shock,
      custom_shock_override: editDraft?.use_custom_shock ? parseFloat(editDraft?.custom_shock_override) : null,
    };
    const { error } = await volatilityService?.update(row?.id, payload);
    setSavingEdit(false);
    if (error) {
      onToast?.('Error al guardar cambios', 'error');
    } else {
      onToast?.('Parametros actualizados', 'success');
      setEditingId(null);
      setEditDraft({});
      await fetchData();
    }
  };

  // ─── Add Row ─────────────────────────────────────────────────────────────────

  const handleAdd = async (newRow) => {
    const { error } = await volatilityService?.create(newRow);
    if (error) {
      onToast?.('Error al agregar parametro', 'error');
    } else {
      onToast?.('Parametro agregado correctamente', 'success');
      setShowAddModal(false);
      await fetchData();
    }
  };

  // ─── Delete Row ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await volatilityService?.delete(deleteTarget?.id);
    setDeleting(false);
    if (error) {
      onToast?.('Error al eliminar parametro', 'error');
    } else {
      onToast?.('Parametro eliminado', 'success');
      setDeleteTarget(null);
      await fetchData();
    }
  };

  // ─── Global Shock ─────────────────────────────────────────────────────────────

  const validateGlobal = (g) => {
    const errs = {};
    ['shock_scenario_mild_pct', 'shock_scenario_moderate_pct', 'shock_scenario_severe_pct', 'eurusd_shock_pct']?.forEach(k => {
      const v = parseFloat(g?.[k]);
      if (isNaN(v) || v < -100 || v > 0) errs[k] = 'Debe estar entre -100 y 0';
    });
    return errs;
  };

  const handleSaveGlobal = async () => {
    const errs = validateGlobal(global);
    if (Object.keys(errs)?.length > 0) { setGlobalErrors(errs); return; }
    setSavingGlobal(true);
    const { error } = await globalShockService?.upsert(global);
    setSavingGlobal(false);
    if (error) {
      onToast?.('Error al guardar escenarios globales', 'error');
    } else {
      onToast?.('Escenarios globales guardados', 'success');
      setGlobalErrors({});
    }
  };

  const existingMetalIds = rows?.map(r => r?.metal_id);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-foreground mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.125rem', fontWeight: '600' }}>Parametros de Shock y Volatilidad</h3>
        <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Gestiona escenarios de shock globales y volatilidad especifica por metal para simulaciones de riesgo.</p>
      </div>
      {/* ── Global Shock Scenarios Panel ── */}
      <div className="mb-8 p-5 bg-card border border-border rounded-xl">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={18} color="var(--color-accent)" strokeWidth={2} />
            <h4 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Escenarios de Shock Globales</h4>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Volatilidad por metal</span>
              <Toggle
                checked={global?.use_per_metal_volatility}
                onChange={v => setGlobal(prev => ({ ...prev, use_per_metal_volatility: v }))}
                label="Alternar modo de volatilidad"
              />
            </div>
            <button
              onClick={handleSaveGlobal}
              disabled={savingGlobal}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm disabled:opacity-60 transition-colors"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', fontWeight: '600' }}
            >
              {savingGlobal
                ? <Icon name="Loader2" size={13} color="white" strokeWidth={2} className="animate-spin" />
                : <Icon name="Save" size={13} color="white" strokeWidth={2.5} />
              }
              {savingGlobal ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'shock_scenario_mild_pct', label: 'Shock Leve (-3%)', icon: 'Minus', color: 'var(--color-warning)', tip: 'Escenario de caída leve del benchmark (-3%). Usado en stress tests del Dashboard y la Calculadora para mostrar la pérdida potencial si el mercado cae ese porcentaje.' },
            { key: 'shock_scenario_moderate_pct', label: 'Shock Moderado (-5%)', icon: 'TrendingDown', color: 'var(--color-error)', tip: 'Escenario de caída moderada del benchmark (-5%). Calculado sobre la exposición total ponderada por yield.' },
            { key: 'shock_scenario_severe_pct', label: 'Shock Severo (-10%)', icon: 'AlertCircle', color: '#7C3AED', tip: 'Escenario de caída severa del benchmark (-10%). Muestra la pérdida potencial máxima en condiciones de estrés extremo.' },
            { key: 'eurusd_shock_pct', label: 'Shock EUR/USD', icon: 'DollarSign', color: 'var(--color-accent)', tip: 'Shock sobre el tipo de cambio EUR/USD. Afecta a todos los precios LME expresados en USD al convertirlos a EUR.' },
          ]?.map(scenario => (
            <div key={scenario?.key} className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-3">
                <Icon name={scenario?.icon} size={14} color={scenario?.color} strokeWidth={2.5} />
                <span className="text-foreground font-medium" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>
                  <Tooltip description={scenario?.tip}>
                    {scenario?.label}
                  </Tooltip>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={global?.[scenario?.key]}
                  onChange={e => {
                    setGlobal(prev => ({ ...prev, [scenario?.key]: parseFloat(e?.target?.value) || 0 }));
                    setGlobalErrors(prev => ({ ...prev, [scenario?.key]: undefined }));
                  }}
                  min={-100} max={0} step={0.5}
                  className={`flex-1 bg-background border rounded px-2 py-1.5 text-foreground focus:outline-none ${
                    globalErrors?.[scenario?.key] ? 'border-error' : 'border-border focus:border-accent'
                  }`}
                  style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}
                />
                <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>%</span>
              </div>
              {globalErrors?.[scenario?.key] && (
                <p className="text-error mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem' }}>{globalErrors?.[scenario?.key]}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* ── Shock Scenarios Table ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="BarChart2" size={18} color="var(--color-accent)" strokeWidth={2} />
            <h4 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>Volatilidad por Metal</h4>
            {!loading && (
              <span className="px-2 py-0.5 rounded-full text-muted-foreground bg-muted" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>
                {rows?.length} metal{rows?.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors"
            style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            <Icon name="Plus" size={15} color="white" strokeWidth={2.5} />
            Agregar metal
          </button>
        </div>

        <div className="rounded-xl border border-border overflow-hidden" style={{ background: 'var(--color-card)' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border" style={{ background: 'var(--color-muted)' }}>
                  {[
                    { h: 'Metal', tip: null },
                    { h: 'Volatilidad 7d (%)', tip: { description: 'Volatilidad histórica del metal en la ventana de 7 días. Se usa para calcular el buffer de riesgo dinámico (VaR). A mayor volatilidad, mayor buffer.' } },
                    { h: 'Volatilidad 14d (%)', tip: { description: 'Volatilidad histórica del metal en la ventana de 14 días. Se usa para calcular el buffer de riesgo dinámico (VaR). A mayor volatilidad, mayor buffer.' } },
                    { h: 'Volatilidad 30d (%)', tip: { description: 'Volatilidad histórica del metal en la ventana de 30 días. Se usa para calcular el buffer de riesgo dinámico (VaR). A mayor volatilidad, mayor buffer.' } },
                    { h: 'Override Shock (%)', tip: { title: 'Shock personalizado', description: 'Shock personalizado que sobreescribe la volatilidad calculada. Úsalo si tienes criterio propio sobre el riesgo del metal.' } },
                    { h: 'Shock Custom', tip: { title: 'Activar shock manual', description: 'Activado: usa el shock manual. Desactivado: usa la volatilidad histórica calculada automáticamente desde los ticks de mercado.' } },
                    { h: 'Acciones', tip: null },
                  ]?.map(col => (
                    <th key={col?.h} className="px-4 py-3 text-left text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                      {col?.tip ? (
                        <Tooltip title={col?.tip?.title} description={col?.tip?.description}>
                          {col?.h}
                        </Tooltip>
                      ) : col?.h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(4)]?.map((_, i) => <SkeletonRow key={i} />)
                ) : rows?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="BarChart2" size={32} color="var(--color-muted-foreground)" strokeWidth={1.5} />
                        <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Sin parametros configurados. Agrega metales para comenzar.</p>
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                          style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
                        >
                          <Icon name="Plus" size={14} color="white" strokeWidth={2.5} />
                          Agregar primer metal
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows?.map(row => {
                    const isEditing = editingId === row?.id;
                    return (
                      <tr key={row?.id} className={`border-b border-border transition-colors ${
                        isEditing ? 'bg-accent/5' : 'hover:bg-muted/40'
                      }`}>
                        {/* Metal name */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row?.metalColor }} />
                            <span className="font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>{row?.metalName}</span>
                          </div>
                        </td>
                        {/* Volatility 7d */}
                        <td className="px-4 py-3">
                          <NumericCell
                            value={isEditing ? editDraft?.volatility_pct_7d : row?.volatility_pct_7d}
                            editing={isEditing}
                            onChange={v => setEditDraft(p => ({ ...p, volatility_pct_7d: v }))}
                            min={0} max={100}
                            error={editErrors?.volatility_pct_7d}
                          />
                        </td>
                        {/* Volatility 14d */}
                        <td className="px-4 py-3">
                          <NumericCell
                            value={isEditing ? editDraft?.volatility_pct_14d : row?.volatility_pct_14d}
                            editing={isEditing}
                            onChange={v => setEditDraft(p => ({ ...p, volatility_pct_14d: v }))}
                            min={0} max={100}
                            error={editErrors?.volatility_pct_14d}
                          />
                        </td>
                        {/* Volatility 30d */}
                        <td className="px-4 py-3">
                          <NumericCell
                            value={isEditing ? editDraft?.volatility_pct_30d : row?.volatility_pct_30d}
                            editing={isEditing}
                            onChange={v => setEditDraft(p => ({ ...p, volatility_pct_30d: v }))}
                            min={0} max={100}
                            error={editErrors?.volatility_pct_30d}
                          />
                        </td>
                        {/* Custom shock override */}
                        <td className="px-4 py-3">
                          {isEditing ? (
                            (editDraft?.use_custom_shock ? (
                              <NumericCell
                                value={editDraft?.custom_shock_override}
                                editing={true}
                                onChange={v => setEditDraft(p => ({ ...p, custom_shock_override: v }))}
                                min={0} max={50} step={0.5}
                                error={editErrors?.custom_shock_override}
                              />
                            ) : (
                              <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>—</span>
                            ))
                          ) : (
                            row?.use_custom_shock ? (
                              <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}>
                                {parseFloat(row?.custom_shock_override)?.toFixed(2)}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>—</span>
                            )
                          )}
                        </td>
                        {/* Use custom shock toggle */}
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Toggle
                              checked={editDraft?.use_custom_shock}
                              onChange={v => setEditDraft(p => ({ ...p, use_custom_shock: v }))}
                              label="Shock personalizado"
                            />
                          ) : (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                row?.use_custom_shock
                                  ? 'bg-accent/15 text-accent' :'bg-muted text-muted-foreground'
                              }`}
                              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}
                            >
                              {row?.use_custom_shock ? 'Activo' : 'Inactivo'}
                            </span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => confirmEdit(row)}
                                disabled={savingEdit}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs disabled:opacity-60 transition-colors"
                                style={{ background: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif', fontWeight: '600' }}
                                title="Guardar"
                              >
                                {savingEdit
                                  ? <Icon name="Loader2" size={12} color="white" strokeWidth={2} className="animate-spin" />
                                  : <Icon name="Check" size={12} color="white" strokeWidth={2.5} />
                                }
                                Guardar
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-foreground text-xs bg-muted border border-border hover:bg-border transition-colors"
                                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                                title="Cancelar"
                              >
                                <Icon name="X" size={12} color="currentColor" strokeWidth={2.5} />
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => startEdit(row)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                                title="Editar"
                              >
                                <Icon name="Pencil" size={15} color="currentColor" strokeWidth={2} />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(row)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-error hover:bg-error/10 transition-colors"
                                title="Eliminar"
                              >
                                <Icon name="Trash2" size={15} color="currentColor" strokeWidth={2} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* ── Modals ── */}
      {showAddModal && (
        <AddRowModal
          metals={metals}
          existingMetalIds={existingMetalIds}
          onClose={() => setShowAddModal(false)}
          onSave={handleAdd}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmDialog
          metalName={deleteTarget?.metalName}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}
